import React, { useState, useEffect, useCallback } from 'react';
import api from '../../services/api';
import './UserManagementModule.css';

export default function UserManagementModule() {
  const [users, setUsers]       = useState([]);
  const [loading, setLoading]   = useState(true);
  const [newEmail, setNewEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [creating, setCreating] = useState(false);
  const [message, setMessage]   = useState(null); // { type: 'success'|'error', text }

  // ── Fetch all users ────────────────────────────────────────────────────────

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await api.get('/auth/users');
      setUsers(resp.data.users);
    } catch {
      setMessage({ type: 'error', text: 'Failed to load users. Please refresh.' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  // ── Create user ────────────────────────────────────────────────────────────

  const handleCreateUser = async (e) => {
    e.preventDefault();
    if (!newEmail || !newPassword) return;
    setCreating(true);
    setMessage(null);
    try {
      const resp = await api.post('/auth/users', {
        email: newEmail,
        password: newPassword,
      });
      setMessage({
        type: 'success',
        text: resp.data.message || `User ${newEmail} created successfully.`,
      });
      setNewEmail('');
      setNewPassword('');
      fetchUsers();
    } catch (err) {
      setMessage({
        type: 'error',
        text: err.response?.data?.detail || 'Failed to create user.',
      });
    } finally {
      setCreating(false);
    }
  };

  // ── Deactivate user ────────────────────────────────────────────────────────

  const handleDeactivate = async (userId, email) => {
    if (!window.confirm(`Deactivate ${email}? They will no longer be able to log in.`)) return;
    setMessage(null);
    try {
      await api.delete(`/auth/users/${userId}`);
      setMessage({ type: 'success', text: `${email} has been deactivated.` });
      fetchUsers();
    } catch (err) {
      setMessage({
        type: 'error',
        text: err.response?.data?.detail || 'Failed to deactivate user.',
      });
    }
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="um-container">
      {/* Header */}
      <div className="um-header">
        <div className="um-header-left">
          <div className="um-header-icon">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                 strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
              <circle cx="9" cy="7" r="4"/>
              <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
              <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
            </svg>
          </div>
          <div>
            <h2 className="um-title">User Management</h2>
            <p className="um-subtitle">Create and manage FraudLens user accounts</p>
          </div>
        </div>
        <div className="um-user-count">{users.length} user{users.length !== 1 ? 's' : ''}</div>
      </div>

      {/* Create User Form */}
      <div className="um-card">
        <div className="um-card-title">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
               strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10"/>
            <line x1="12" y1="8" x2="12" y2="16"/>
            <line x1="8" y1="12" x2="16" y2="12"/>
          </svg>
          Add New User
        </div>
        <form onSubmit={handleCreateUser} className="um-create-form">
          <div className="um-field-group">
            <div className="um-field">
              <label className="um-label" htmlFor="new-email">Email Address</label>
              <input
                id="new-email"
                className="um-input"
                type="email"
                placeholder="user@company.com"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                required
                autoComplete="off"
              />
            </div>
            <div className="um-field">
              <label className="um-label" htmlFor="new-password">Password</label>
              <input
                id="new-password"
                className="um-input"
                type="password"
                placeholder="Min. 8 characters"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                minLength={8}
                autoComplete="new-password"
              />
            </div>
          </div>
          <button className="um-btn-primary" type="submit" disabled={creating}>
            {creating ? (
              <>
                <span className="um-spinner" />
                Creating…
              </>
            ) : (
              <>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                     strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                  <circle cx="8.5" cy="7" r="4"/>
                  <line x1="20" y1="8" x2="20" y2="14"/>
                  <line x1="23" y1="11" x2="17" y2="11"/>
                </svg>
                Create User
              </>
            )}
          </button>
        </form>
      </div>

      {/* Feedback message */}
      {message && (
        <div className={`um-message um-message-${message.type}`} role="alert">
          {message.type === 'success' ? (
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                 strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="20 6 9 17 4 12"/>
            </svg>
          ) : (
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                 strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10"/>
              <line x1="12" y1="8" x2="12" y2="12"/>
              <line x1="12" y1="16" x2="12.01" y2="16"/>
            </svg>
          )}
          {message.text}
        </div>
      )}

      {/* Users table */}
      <div className="um-card">
        <div className="um-card-title">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
               strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
            <circle cx="9" cy="7" r="4"/>
            <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
            <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
          </svg>
          All Users
        </div>

        {loading ? (
          <div className="um-loading">
            <span className="um-spinner" />
            Loading users…
          </div>
        ) : users.length === 0 ? (
          <div className="um-empty">
            No users yet. Create one above.
          </div>
        ) : (
          <div className="um-table-wrap">
            <table className="um-table">
              <thead>
                <tr>
                  <th>Email</th>
                  <th>Status</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id} className={!u.is_active ? 'um-row-inactive' : ''}>
                    <td className="um-cell-email">
                      {u.email}
                      {u.is_admin && <span className="um-badge-admin">Admin</span>}
                    </td>
                    <td>
                      <span className={`um-badge um-badge-${u.is_active ? 'active' : 'inactive'}`}>
                        {u.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="um-cell-date">
                      {u.created_at ? u.created_at.slice(0, 10) : '—'}
                    </td>
                    <td className="um-cell-actions">
                      {u.is_active && (
                        <button
                          className="um-btn-danger"
                          onClick={() => handleDeactivate(u.id, u.email)}
                          title="Deactivate user"
                        >
                          Deactivate
                        </button>
                      )}
                      {!u.is_active && (
                        <span className="um-deactivated-label">Deactivated</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
